import React, { createContext, useContext, useEffect, useState } from 'react';

type Theme = 'dark' | 'light';

interface ThemeContextValue {
  theme: Theme;
  isDark: boolean;
  toggle: () => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: 'dark',
  isDark: true,
  toggle: () => {},
});

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setTheme] = useState<Theme>(() => {
    const saved = localStorage.getItem('theme');
    return (saved === 'light' || saved === 'dark') ? saved : 'dark';
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggle = () => setTheme(t => t === 'dark' ? 'light' : 'dark');

  return (
    <ThemeContext.Provider value={{ theme, isDark: theme === 'dark', toggle }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);

/** Returns an object of CSS variable references for inline styles.
 *  Use as: style={{ background: C.bgCard, color: C.textPrimary }} */
export function useThemeColors() {
  return THEME_VARS;
}

// All values are CSS var() references — they resolve to the correct color
// for the active theme via data-theme on <html>.
export const THEME_VARS = {
  bgPage:         'var(--bg-page)',
  bgSidebar:      'var(--bg-sidebar)',
  bgCard:         'var(--bg-card)',
  bgDeep:         'var(--bg-deep)',
  bgElement:      'var(--bg-element)',
  bgInput:        'var(--bg-input)',
  border:         'var(--border)',
  borderSubtle:   'var(--border-subtle)',
  textPrimary:    'var(--text-primary)',
  textSecondary:  'var(--text-secondary)',
  textMuted:      'var(--text-muted)',
  textFaint:      'var(--text-faint)',
  accent:         '#5B4FE8',
  accentPurple:   '#7c3aed',
  green:          '#10b981',
  amber:          '#f59e0b',
  red:            '#ef4444',
} as const;
